import { useCallback } from 'react';

import { ModelViewerElement } from '@google/model-viewer';

import { dataURLToBlob } from '../../../../utils/dataURLToBlob';
import { downloadDataURL } from '../../../../utils/downloadDataURL';
import { replaceFileExtension } from '../../../../utils/replaceFileExtension';
import { compositeScreenshot } from '../../utils/compositeScreenshot';

import { useModelViewer } from '../useModelViewer';

import {
  ScreenshotMimeType,
  ScreenshotOptions,
  ScreenshotResult
} from './useModelViewerScreenshot.types';

const DEFAULT_MIME_TYPE: ScreenshotMimeType = 'image/png';
const DEFAULT_QUALITY = 0.92;
// model-viewer's default minimumRenderScale; its static getter always returns
// undefined, so the value to restore cannot be read back.
const DEFAULT_MIN_RENDER_SCALE = 0.5;
const FULL_SCALE_FRAME_TIMEOUT_MS = 2000;

type ModelViewerScene = { scaleStep: number };

/**
 * Reads the canvas only from a frame rendered at full scale.
 *
 * While frames are slow model-viewer lowers the render scale (down to 0.5) and
 * draws the shrunken frame into the top-left corner of the full-size canvas,
 * stretching it back up with CSS. toDataURL() reads the raw canvas, so a
 * throttled frame comes out downscaled into the corner with the rest empty.
 * Pinning minimumRenderScale to 1 makes the next rendered frame full scale;
 * scene.scaleStep === 0 means the last frame actually was.
 */
async function captureFullScaleDataURL(
  modelViewerEl: ModelViewerElement,
  scene: ModelViewerScene | null,
  mimeType: string,
  quality: number
): Promise<string> {
  const toDataURL = () =>
    (modelViewerEl as any).toDataURL(mimeType, quality) as string;

  if (!scene) return toDataURL();

  const ModelViewer = modelViewerEl.constructor as unknown as {
    minimumRenderScale: number;
  };
  ModelViewer.minimumRenderScale = 1;

  try {
    const deadline = performance.now() + FULL_SCALE_FRAME_TIMEOUT_MS;
    while (scene.scaleStep !== 0) {
      if (performance.now() > deadline) {
        throw new Error('Screenshot is not ready, please try again');
      }
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
    return toDataURL();
  } finally {
    ModelViewer.minimumRenderScale = DEFAULT_MIN_RENDER_SCALE;
  }
}

/**
 * Converts a data URL to an HTMLCanvasElement with the image drawn on it.
 * Used to convert model-viewer's toDataURL output into a canvas for compositing.
 */
function dataURLToCanvas(dataURL: string): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Cannot get 2D context'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas);
    };
    img.onerror = () => reject(new Error('Failed to load screenshot frame'));
    img.src = dataURL;
  });
}

/**
 * Centre-crops a canvas to the given ratio. Returns the source canvas
 * untouched when it already matches (within a sub-pixel tolerance).
 */
function cropCanvasToAspectRatio(
  source: HTMLCanvasElement,
  ratio: { width: number; height: number }
): HTMLCanvasElement {
  const target = ratio.width / ratio.height;
  if (!(target > 0) || !source.width || !source.height) return source;

  const current = source.width / source.height;
  let width = source.width;
  let height = source.height;

  if (current > target) {
    width = Math.round(source.height * target);
  } else if (current < target) {
    height = Math.round(source.width / target);
  }

  if (width === source.width && height === source.height) return source;

  const cropped = document.createElement('canvas');
  cropped.width = Math.max(1, width);
  cropped.height = Math.max(1, height);
  const ctx = cropped.getContext('2d');
  if (!ctx) return source;

  ctx.drawImage(
    source,
    Math.round((source.width - cropped.width) / 2),
    Math.round((source.height - cropped.height) / 2),
    cropped.width,
    cropped.height,
    0,
    0,
    cropped.width,
    cropped.height
  );

  return cropped;
}

function useModelViewerScreenshot() {
  const { modelViewerEl, getHiddenProp } = useModelViewer();

  const getScreenshot = useCallback(
    async (options: ScreenshotOptions = {}): Promise<ScreenshotResult> => {
      if (!modelViewerEl) throw new Error('ModelViewer is not mounted');

      const {
        mimeType = DEFAULT_MIME_TYPE,
        quality = DEFAULT_QUALITY,
        withBackground,
        cropToAspectRatio
      } = options;

      const scene = getHiddenProp<ModelViewerScene>('scene');

      if (withBackground) {
        // Use model-viewer's toDataURL to correctly capture the current
        // rendered frame (preserves camera position, handles preserveDrawingBuffer).
        const rawDataURL = await captureFullScaleDataURL(
          modelViewerEl,
          scene,
          'image/png',
          1.0
        );
        const rawCanvas = await dataURLToCanvas(rawDataURL);
        // Crop before compositing so the background fills the cropped frame
        // instead of being cut along with the model.
        const modelCanvas = cropToAspectRatio
          ? cropCanvasToAspectRatio(rawCanvas, cropToAspectRatio)
          : rawCanvas;
        const { blob, dataURL } = await compositeScreenshot(
          modelCanvas,
          withBackground
        );
        return {
          dataURL,
          blob,
          width: modelCanvas.width,
          height: modelCanvas.height
        };
      }

      const rawDataURL = await captureFullScaleDataURL(
        modelViewerEl,
        scene,
        mimeType,
        quality
      );

      if (cropToAspectRatio) {
        const rawCanvas = await dataURLToCanvas(rawDataURL);
        const cropped = cropCanvasToAspectRatio(rawCanvas, cropToAspectRatio);
        const dataURL = cropped.toDataURL(mimeType, quality);
        return {
          dataURL,
          blob: dataURLToBlob(dataURL),
          width: cropped.width,
          height: cropped.height
        };
      }

      return {
        dataURL: rawDataURL,
        blob: dataURLToBlob(rawDataURL),
        width: modelViewerEl.clientWidth,
        height: modelViewerEl.clientHeight
      };
    },
    [modelViewerEl, getHiddenProp]
  );

  const captureScreenshot = useCallback(
    async (options: ScreenshotOptions = {}): Promise<void> => {
      const mimeType = options.mimeType ?? DEFAULT_MIME_TYPE;
      const rawName = options.filename ?? 'screenshot';
      const extension = options.withBackground ? 'png' : mimeType.split('/')[1];
      const filename = replaceFileExtension(rawName, extension);
      const result = await getScreenshot(options);
      downloadDataURL(result.dataURL, filename);
    },
    [getScreenshot]
  );

  return { captureScreenshot, getScreenshot };
}

export default useModelViewerScreenshot;
